import { strEnum } from '../helpers';


// Unit categories (temperature, length, etc.)
// -----------------------------------------------------------------------------

export const TEMPERATURE = 'TEMPERATURE';
export const LENGTH = 'LENGTH';

export const UnitCategory = strEnum([
  TEMPERATURE,
  LENGTH
]);

export type UnitCategory = keyof typeof UnitCategory;


// Length units
// -----------------------------------------------------------------------------

export const METERS = 'METERS';
export const KILOMETERS = 'KILOMETERS';
export const FEET = 'FEET';
export const NAUTICAL_MILES = 'NAUTICAL_MILES';
export const STATUTE_MILES = 'STATUTE_MILES';

export const LengthUnit = strEnum([
  METERS,
  KILOMETERS,
  FEET,
  NAUTICAL_MILES,
  STATUTE_MILES
]);

export type LengthUnit = keyof typeof LengthUnit;


// Temperature units
// -----------------------------------------------------------------------------

export const CELSIUS = 'CELSIUS';
export const FAHRENHEIT = 'FAHRENHEIT';

export const TemperatureUnit = strEnum([
  CELSIUS,
  FAHRENHEIT
]);

export type TemperatureUnit = keyof typeof TemperatureUnit;


// Unit Category -> Unit mappings
// -----------------------------------------------------------------------------

export const UNIT_CATEGORY_MAPPINGS: Map<string, Array<string>> = new Map<string, Array<string>>([
  [LENGTH, Object.keys(LengthUnit)],
  [TEMPERATURE, Object.keys(TemperatureUnit)]
]);


// Helper types/methods
// -----------------------------------------------------------------------------

/**
 * Helper type that allows referencing all unit types via a single root type (for example, UNITS.TEMPERATURE.CELSIUS).
 */
export const UNITS = {
  TEMPERATURE: TemperatureUnit,
  LENGTH: LengthUnit
};


/**
 * Helper type that is a union of all valid unit types.
 */
export type Unit = TemperatureUnit | LengthUnit;


/**
 * Returns the list of valid units (e.g., 'METER', 'FEET') for a given unit category (e.g., 'LENGTH').
 * @param {UnitCategory} category Unit category
 * @return {Array<string>} List of valid units for category
 */
export function getCategoryUnits(category: UnitCategory): Array<string> {
  return UNIT_CATEGORY_MAPPINGS.get(category);
}


/**
 * Returns the category (length, temperature, etc.) of the given unit, or null if cannot be determined.
 * @param {Unit} unit
 */
export function getUnitCategory(unit: Unit): UnitCategory {
  for (const category of Object.keys(UnitCategory)) {
    const categoryUnits = UNIT_CATEGORY_MAPPINGS.get(category);
    if (categoryUnits.includes(unit)) {
      return category as UnitCategory;
    }
  }
  return null;
}
